import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscriptionAPI } from '../services/api';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/**
 * Normalise the features payload coming from the API.
 * The API may return:
 *   - An array of objects  [{ key, enabled, ... }]
 *   - An object            { someKey: true, ... }
 * We always store it as an object { key: boolean } for fast lookup.
 */
function normaliseFeatures(raw) {
  if (!raw) return {};
  if (Array.isArray(raw)) {
    const obj = {};
    raw.forEach((f) => { if (f.key) obj[f.key] = !!f.enabled; });
    return obj;
  }
  if (typeof raw === 'object') return raw;
  return {};
}

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Derived state
  const plan = subscription?.plan || null;
  const features = normaliseFeatures(subscription?.features || plan?.features);
  const limits = subscription?.limits || plan?.limits || {};
  const status = subscription?.subscription?.status || subscription?.status || 'inactive';

  // Auto-fetch subscription when token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchSubscription();
    } else {
      setLoading(false);
    }

    // Listen for login/logout events
    const onStorage = (e) => {
      if (e.key === 'token') {
        if (e.newValue) fetchSubscription();
        else clearSubscription();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await subscriptionAPI.getMy();
      const subData = response.data?.data || response.data;
      setSubscription(subData);
      localStorage.setItem('subscription', JSON.stringify(subData));
    } catch (error) {
      // No subscription found — new user or expired
      console.warn('Subscription fetch failed:', error.response?.status);
      setSubscription(null);
      localStorage.removeItem('subscription');
    } finally {
      setLoading(false);
    }
  };

  const clearSubscription = useCallback(() => {
    setSubscription(null);
    localStorage.removeItem('subscription');
  }, []);

  // Map add-on keys to the feature they grant. Kept in sync with the backend
  // (subscription.controller.js > ADDON_FEATURE_MAP).
  const ADDON_FEATURE_MAP = { custom_domain: 'has_custom_domain' };

  // Check if a feature is accessible (overrides → add-ons → plan features)
  const canAccessFeature = useCallback((featureKey) => {
    // If no subscription loaded yet, be permissive (don't block UI while loading)
    if (!subscription) return true;

    // Feature overrides always win (super admin grants)
    const overrides = subscription.feature_overrides || {};
    if (featureKey in overrides) {
      return overrides[featureKey];
    }

    // When the backend already normalised features with add-on source,
    // `features[featureKey]` reflects the final truth. Still, guard against
    // older backend versions by also checking `subscription.subscription.addons`.
    if (features[featureKey] === true) return true;

    const addons = subscription.subscription?.addons || subscription.addons || [];
    const grantedByActiveAddon = addons.some(
      (a) => a.status === 'active' && ADDON_FEATURE_MAP[a.key] === featureKey
    );
    if (grantedByActiveAddon) return true;

    return features[featureKey] === true;
  }, [subscription, features]);

  // Check if a page is accessible based on plan's allowedPages
  const canAccessPage = useCallback((pageKey) => {
    if (!plan) return true; // be permissive while loading

    const allowedPages = plan.allowed_pages || plan.allowedPages || [];
    // If no pages are defined, allow all (open plan)
    if (allowedPages.length === 0) return true;

    return allowedPages.includes(pageKey);
  }, [plan]);

  // Get remaining branches based on limits and usage
  const getRemainingBranches = useCallback(() => {
    const maxBranches = limits.maxBranches || limits.max_branches || 1;
    if (maxBranches === -1) return Infinity;
    const usedBranches = subscription?.usage?.branches || 0;
    return Math.max(0, maxBranches - usedBranches);
  }, [limits, subscription]);

  // Get remaining users based on limits and usage
  const getRemainingUsers = useCallback(() => {
    const maxUsers = limits.maxUsers || limits.max_users || 5;
    if (maxUsers === -1) return Infinity;
    const usedUsers = subscription?.usage?.users || 0;
    return Math.max(0, maxUsers - usedUsers);
  }, [limits, subscription]);

  // Get remaining devices based on limits and usage
  const getRemainingDevices = useCallback(() => {
    const maxDevices = limits.maxDevices || limits.max_devices || 3;
    if (maxDevices === -1) return Infinity;
    const additionalDevices = subscription?.subscription?.additionalDevices || subscription?.additionalDevices || 0;
    const usedDevices = subscription?.usage?.devices || 0;
    return Math.max(0, (maxDevices + additionalDevices) - usedDevices);
  }, [limits, subscription]);

  // Days until subscription expires (works for both trial and active)
  const daysUntilExpiry = useCallback(() => {
    const endDate = subscription?.subscription?.endDate || subscription?.endDate
      || subscription?.subscription?.trialEndDate || subscription?.trialEndDate;
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  }, [subscription]);

  // Is subscription expiring within 15 days?
  const isExpiringSoon = useCallback(() => {
    const days = daysUntilExpiry();
    return days !== null && days <= 15 && days >= 0;
  }, [daysUntilExpiry]);

  // Check if trial is expiring within 7 days (kept for backward compat)
  const isTrialExpiringSoon = useCallback(() => {
    if (status !== 'trial' && status !== 'trialing') return false;
    const days = daysUntilExpiry();
    return days !== null && days <= 7 && days >= 0;
  }, [status, daysUntilExpiry]);

  // Re-fetch subscription from API
  const refreshSubscription = useCallback(async () => {
    await fetchSubscription();
  }, []);

  const value = {
    subscription,
    plan,
    features,
    limits,
    status,
    loading,
    canAccessFeature,
    canAccessPage,
    getRemainingBranches,
    getRemainingUsers,
    getRemainingDevices,
    daysUntilExpiry,
    isExpiringSoon,
    isTrialExpiringSoon,
    refreshSubscription,
    clearSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
